// MapUtils.js - Utility functions for map operations
import artIcon from './icons/art.png';
import businessIcon from './icons/business.png';
import concertIcon from './icons/concert.png';
import educationIcon from './icons/education.png';
import expoIcon from './icons/expo.png';
import otherIcon from './icons/other.png';
import promoIcon from './icons/promo.png';
import sportIcon from './icons/sport.png';
import travelIcon from './icons/travel.png';
import meetupsIcon from './icons/meetups.png';
import reviewIcon from './icons/review.png';
import ArtTrackIcon from '@mui/icons-material/ArtTrack';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EventIcon from '@mui/icons-material/Event';
import GroupsIcon from '@mui/icons-material/Groups';
import HotelIcon from '@mui/icons-material/Hotel';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PlaceIcon from '@mui/icons-material/Place';
import RateReviewIcon from '@mui/icons-material/RateReview';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SchoolIcon from '@mui/icons-material/School';
import SpaIcon from '@mui/icons-material/Spa';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import VideocamIcon from '@mui/icons-material/Videocam';

export const MARKER_BADGE_ICONS = {
  art: ArtTrackIcon,
  business: BusinessCenterIcon,
  cafe: LocalCafeIcon,
  event: EventIcon,
  expo: StorefrontIcon,
  hotel: HotelIcon,
  meetup: GroupsIcon,
  music: MusicNoteIcon,
  nightlife: LocalBarIcon,
  offer: LocalOfferIcon,
  photo: PhotoCameraIcon,
  place: PlaceIcon,
  restaurant: RestaurantIcon,
  review: RateReviewIcon,
  school: SchoolIcon,
  spa: SpaIcon,
  sport: SportsSoccerIcon,
  ticket: ConfirmationNumberIcon,
  travel: TravelExploreIcon,
  venue: StorefrontIcon,
  video: VideocamIcon,
  celebration: CelebrationIcon,
};

const normalizeCategoryText = (...values) => (
  values
    .filter(value => value !== null && value !== undefined)
    .map(value => String(value).trim().toLowerCase())
    .filter(Boolean)
    .join(' ')
);

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const hasCategoryTerm = (text, terms) => (
  terms.some((term) => {
    const pattern = escapeRegExp(term.toLowerCase()).replace(/\s+/g, '\\s+');
    return new RegExp(`(^|[^a-z0-9])${pattern}([^a-z0-9]|$)`).test(text);
  })
);

export const getMarkerDataType = (place = {}) => {
  const source = String(place._source || '').toLowerCase();
  if (source === 'review' || source === 'venue') return source;

  const categoryText = normalizeCategoryText(place.main_category, place.category);
  if (categoryText.includes('review')) return 'review';
  if (categoryText.includes('venue') || categoryText.includes('place')) return 'venue';

  return 'event';
};

const getEventBadgeIconKey = (place = {}) => {
  const text = normalizeCategoryText(
    place.sub_category,
    place.type,
    place.main_category,
    place.category
  );

  if (hasCategoryTerm(text, ['promotion', 'promotions', 'coupon', 'coupons', 'sale', 'discount'])) return 'offer';
  if (hasCategoryTerm(text, ['ticket', 'tickets'])) return 'ticket';
  if (hasCategoryTerm(text, ['art', 'arts', 'film', 'films'])) return 'art';
  if (hasCategoryTerm(text, ['business', 'investment'])) return 'business';
  if (hasCategoryTerm(text, ['performance', 'performances', 'concert', 'music', 'live'])) return 'music';
  if (hasCategoryTerm(text, ['education', 'family', 'kid', 'kids'])) return 'school';
  if (hasCategoryTerm(text, ['fair', 'exhibition', 'expo', 'product', 'service'])) return 'expo';
  if (hasCategoryTerm(text, ['sport', 'sports', 'competition', 'competitions', 'game', 'games'])) return 'sport';
  if (hasCategoryTerm(text, ['travel', 'photo', 'photos', 'tour', 'tours'])) return 'travel';
  if (hasCategoryTerm(text, ['meetup', 'meetups', 'networking', 'social', 'small group'])) return 'meetup';
  if (hasCategoryTerm(text, ['night', 'night-time', 'nighttime'])) return 'nightlife';
  if (hasCategoryTerm(text, ['day', 'day-time', 'daytime'])) return 'celebration';

  return 'event';
};

const getReviewBadgeIconKey = (place = {}) => {
  const categoryText = normalizeCategoryText(
    place.place_type,
    place.review_type,
    place.sub_category,
    place.category,
    place.main_category
  );

  if (hasCategoryTerm(categoryText, ['food', 'restaurant', 'street food'])) return 'restaurant';
  if (hasCategoryTerm(categoryText, ['cafe', 'coffee'])) return 'cafe';
  if (hasCategoryTerm(categoryText, ['hotel', 'accommodation'])) return 'hotel';
  if (hasCategoryTerm(categoryText, ['beauty', 'wellness', 'spa', 'massage'])) return 'spa';
  if (hasCategoryTerm(categoryText, ['transport', 'transportation', 'travel'])) return 'travel';
  if (hasCategoryTerm(categoryText, ['product', 'shop', 'fashion', 'electronics'])) return 'venue';
  if (hasCategoryTerm(categoryText, ['service'])) return 'business';

  const contentType = normalizeCategoryText(place.main_content_type, place.content_type);
  if (hasCategoryTerm(contentType, ['video', 'video_link'])) return 'video';
  if (hasCategoryTerm(contentType, ['image', 'photo'])) return 'photo';

  return 'review';
};

const getVenueBadgeIconKey = (place = {}) => {
  const text = normalizeCategoryText(
    place.venue_type,
    place.cuisine_type,
    place.sub_category,
    place.category,
    place.main_category
  );

  if (hasCategoryTerm(text, ['restaurant', 'food', 'cuisine'])) return 'restaurant';
  if (hasCategoryTerm(text, ['cafe', 'coffee', 'bakery'])) return 'cafe';
  if (hasCategoryTerm(text, ['bar', 'pub', 'club', 'nightclub', 'nightlife', 'rooftop'])) return 'nightlife';
  if (hasCategoryTerm(text, ['spa', 'massage', 'wellness', 'beauty'])) return 'spa';
  if (hasCategoryTerm(text, ['hotel', 'resort', 'hostel'])) return 'hotel';
  if (hasCategoryTerm(text, ['shop', 'store', 'mall', 'market'])) return 'venue';
  if (hasCategoryTerm(text, ['museum', 'gallery', 'art'])) return 'art';
  if (hasCategoryTerm(text, ['tour', 'attraction', 'travel'])) return 'travel';

  return 'place';
};

export const getMarkerBadgeIconKey = (place = {}) => {
  const dataType = getMarkerDataType(place);

  if (dataType === 'review') return getReviewBadgeIconKey(place);
  if (dataType === 'venue') return getVenueBadgeIconKey(place);
  return getEventBadgeIconKey(place);
};

export const getMarkerBadgeIcon = (place = {}) => {
  const iconKey = getMarkerBadgeIconKey(place);
  return MARKER_BADGE_ICONS[iconKey] || MARKER_BADGE_ICONS.event;
};

// --- Marker icon logic ---
export const getMarkerIcon = (place) => {
  if (place._source === 'review') return reviewIcon;

  // Check multiple possible field names for category/type
  // (useFeedBatches uses main_category, old API uses type)
  const eventType = place.type || place.main_category || place.category || '';

  // Use includes() for flexible matching (handles variations like "Fair & Exhibitions" and "Product & Service Fair")
  if (eventType.includes('Art') || eventType.includes('Film')) return artIcon;
  if (eventType.includes('Business') || eventType.includes('Investment')) return businessIcon;
  if (eventType.includes('Performance') || eventType.includes('Concert') || eventType.includes('Music')) return concertIcon;
  if (eventType.includes('Education') || eventType.includes('Family') || eventType.includes('Kid')) return educationIcon;
  if (eventType.includes('Fair') || eventType.includes('Exhibition') || eventType.includes('Expo')) return expoIcon;
  if (eventType.includes('Promotion') || eventType.includes('Sale') || eventType.includes('Discount')) return promoIcon;
  if (eventType.includes('Sport') || eventType.includes('Competition') || eventType.includes('Game')) return sportIcon;
  if (eventType.includes('Travel') || eventType.includes('Photo') || eventType.includes('Tour')) return travelIcon;
  if (eventType.includes('Meetup') || eventType.includes('Networking') || eventType.includes('Social')) return meetupsIcon;
  if (eventType === 'Other') return otherIcon;

  return otherIcon; // Default fallback
};

// Language-specific labels
export const dateLabels = {
  TH: {
    start: 'เริ่มต้น',
    end: 'สิ้นสุด',
    moreEvents: 'อีก {count} กิจกรรม',
    nextEvent: 'กิจกรรมถัดไป',
    prevEvent: 'กิจกรรมก่อนหน้า',
    reviewDate: 'วันที่รีวิว'
  },
  EN: {
    start: 'Start',
    end: 'End',
    moreEvents: '{count} more events',
    nextEvent: 'Next event',
    prevEvent: 'Previous event',
    reviewDate: 'Review Date'
  },
  CN: {
    start: '开始',
    end: '结束',
    moreEvents: '还有{count}个活动',
    nextEvent: '下一个活动',
    prevEvent: '上一个活动',
    reviewDate: '点评日期'
  }
};

// Distance calculation function (Haversine formula)
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a = 
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in meters
};

// Group nearby events (within 30 meters)
export const groupNearbyEvents = (places) => {
  if (!Array.isArray(places) || places.length === 0) return [];

  const groups = [];
  const processed = new Set();

  places.forEach((place, index) => {
    if (processed.has(index)) return;

    // Create stable group ID based on rounded lat/lng coordinates
    const roundedLat = Math.round(place.latitude * 100000) / 100000; // 5 decimal precision
    const roundedLng = Math.round(place.longitude * 100000) / 100000;
    const stableGroupId = `group-${roundedLat}:${roundedLng}`;

    const group = {
      id: stableGroupId,
      mainPlace: place,
      places: [place],
      latitude: place.latitude,
      longitude: place.longitude
    };

    processed.add(index);

    // Check all other places to see if they're within 30 meters
    places.forEach((otherPlace, otherIndex) => {
      if (
        index !== otherIndex && 
        !processed.has(otherIndex) && 
        otherPlace.latitude && 
        otherPlace.longitude
      ) {
        const distance = calculateDistance(
          place.latitude, 
          place.longitude, 
          otherPlace.latitude, 
          otherPlace.longitude
        );

        if (distance <= 100) { // 30 meters threshold
          group.places.push(otherPlace);
          processed.add(otherIndex);
        }
      }
    });

    groups.push(group);
  });

  return groups;
};

// Set moment locale based on language
export const setMomentLocale = (language) => {
  if (language === 'TH') {
    return 'th';
  } else if (language === 'CN') {
    return 'zh-cn';
  } else {
    return 'en';
  }
};

// Format date in the selected language
export const formatDate = (date, momentInstance) => {
  if (!date) return '';
  return momentInstance(date).format('DD-MMM-YY');
};
